
import React, { useEffect } from "react";
import { BadgeDollarSign, ShieldCheck, PiggyBank, Clock, Calendar, CreditCard, FileText, DollarSign, FileSearch, Users } from "lucide-react";
import Footer from "@/components/Footer";
import Navbar from "@/components/Navbar";
import { ServiceSelectionProvider } from "@/contexts/ServiceSelectionContext";
import HeroSection from "@/components/product/HeroSection";
import VideoSection from "@/components/product/VideoSection";
import SegmentsSection from "@/components/product/SegmentsSection";
import BenefitsCarouselSection from "@/components/product/BenefitsCarouselSection";
import HowItWorksSection from "@/components/product/HowItWorksSection";
import TestimonialSection from "@/components/product/TestimonialSection";
import ProductPlanSection from "@/components/product/ProductPlanSection";
import SEO from "@/components/SEO";
import useMetaTracking from "@/hooks/use-meta-tracking";

const BoletoParcelado = () => {
  const { trackViewContent } = useMetaTracking();
  
  useEffect(() => {
    trackViewContent({
      content_name: "boleto_parcelado",
      content_category: "product_page"
    });
  }, [trackViewContent]);

  const steps = [{
    number: "01",
    title: "Cadastre sua clínica",
    description: "Faça cadastro da sua clínica na plataforma Dr.Cash em poucos passos"
  }, {
    number: "02", 
    title: "Selecione o plano",
    description: "Os planos disponíveis são anual, semestral ou mensal"
  }, {
    number: "03",
    title: "Comece!",
    description: "Após a confirmação do pagamento você receberá seu login e senha"
  }];

  const carouselBenefits = [
    {
      icon: <BadgeDollarSign className="w-12 h-12 text-drcash-caribbean-green" />,
      title: "Juros flexíveis",
      description: "Oferta de três modalidades - juros a partir de 1,11%, juros compartilhados entre clínica e paciente, ou sem juros para o paciente."
    },
    {
      icon: <ShieldCheck className="w-12 h-12 text-drcash-caribbean-green" />,
      title: "Sem entrada ou garantia",
      description: "Os pacientes não precisam pagar entrada nem oferecer bens em garantia para financiar procedimentos."
    },
    {
      icon: <PiggyBank className="w-12 h-12 text-drcash-caribbean-green" />,
      title: "Valor máximo aprovado",
      description: "Possibilidade de aprovação de valores altos, aumentando o ticket médio da clínica e permitindo oferecer mais produtos e serviços."
    },
    {
      icon: <Clock className="w-12 h-12 text-drcash-caribbean-green" />,
      title: "Recebimento em 48h",
      description: "A clínica recebe o valor rapidamente, bastando enviar a Nota Fiscal ou comprovante do procedimento."
    },
    {
      icon: <Calendar className="w-12 h-12 text-drcash-caribbean-green" />,
      title: "Carência de 30 dias",
      description: "O paciente tem 30 dias até o vencimento do primeiro boleto."
    },
    {
      icon: <CreditCard className="w-12 h-12 text-drcash-caribbean-green" />,
      title: "Boleto bancário como opção de pagamento",
      description: "Ampliação das formas de pagamento além de cartão de crédito, Pix ou plano de saúde."
    },
    {
      icon: <FileText className="w-12 h-12 text-drcash-caribbean-green" />,
      title: "Parcelamento flexível",
      description: "Opções de 6x a 24x, tornando o valor mensal mais acessível à renda familiar do paciente."
    },
    {
      icon: <DollarSign className="w-12 h-12 text-drcash-caribbean-green" />,
      title: "Valores altos",
      description: "Possibilidade de parcelar procedimentos de R$250 até R$30 mil."
    },
    {
      icon: <FileSearch className="w-12 h-12 text-drcash-caribbean-green" />,
      title: "Análise rápida",
      description: "Verificação digital que determina em segundos se o paciente está elegível ao financiamento."
    },
    {
      icon: <Users className="w-12 h-12 text-drcash-caribbean-green" />,
      title: "Gestão da inadimplência",
      description: "A cobrança e inadimplência são responsabilidade da Dr.Cash, permitindo que a clínica foque em suas atividades principais."
    }
  ];

  const boletoParceladoSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "Boleto Parcelado Dr.Cash",
    "description": "Parcele procedimentos em 24x no boleto com a Dr.Cash e receba em 48h sem se preocupar com inadimplência.",
    "brand": {
      "@type": "Brand",
      "name": "Dr.Cash"
    },
    "offers": {
      "@type": "AggregateOffer",
      "priceCurrency": "BRL",
      "lowPrice": "250",
      "highPrice": "30000",
      "offerCount": "3",
      "offers": [
        {
          "@type": "Offer",
          "name": "Plano Mensal"
        },
        {
          "@type": "Offer",
          "name": "Plano Semestral"
        },
        {
          "@type": "Offer",
          "name": "Plano Anual"
        }
      ]
    }
  };

  return (
    <ServiceSelectionProvider>
      <SEO 
        title="Boleto Parcelado para Clínicas | Parcele em 24x, receba em 48h | Dr.Cash"
        description="Ofereça parcelamento em até 24x no boleto para seus pacientes. A Dr.Cash garante o recebimento em 48h, sem risco de inadimplência para sua clínica."
        keywords="boleto parcelado, financiamento para clínicas, parcelamento procedimentos médicos, boleto parcelado saúde, recebimento em 48h"
        canonical="/boleto-parcelado"
        structuredData={boletoParceladoSchema}
      />
      <Navbar variant="dark" />
      <main>
        <HeroSection 
          title="Pare de perder pacientes, parcele em 24x no boleto e receba em 48h sem se preocupar com inadimplência!"
          description="Com a gente, você descobre em segundos o valor máximo de crédito disponível para seu paciente. E o melhor: ele só paga o primeiro boleto em 30 dias, sem necessidade de garantia de bens."
          ctaText="Credencie sua clínica agora!"
          imageSrc="/lovable-uploads/3b8fcd1b-4453-4f91-b823-84ade19832f01.webp"
          showCalculatorButton={false}
        />
        
        <VideoSection
          title="Veja como funciona"
          videoEmbedId="UclbzD9X0Rc"
        />
        
        <SegmentsSection
          title="Segmentos que atuamos"
          subtitle="Oferecemos soluções financeiras para diversos segmentos da área da saúde"
        />
        
        <BenefitsCarouselSection
          title="Benefícios do Boleto Parcelado"
          subtitle="Conheça todas as vantagens que o Boleto Parcelado oferece para sua clínica"
          benefits={carouselBenefits}
        />
        
        <HowItWorksSection
          title="Cadastre e comece!"
          subtitle="Comece agora mesmo e transforme a gestão financeira da sua clínica!"
          steps={steps}
          ctaTitle="Pronto para aumentar o faturamento da sua clínica oferecendo mais opções de pagamento para seus pacientes?"
          ctaText="Cadastre-se agora"
          disclaimer={{
            title: "Marque seu treinamento",
            description: "Se for necessário, agende um treinamento na própria plataforma"
          }}
        />
        
        <TestimonialSection
          quote="Depois que implementei o Boleto Parcelado da Dr.Cash, minha clínica odontológica aumentou o faturamento em 35%. Pacientes que antes adiavam tratamentos de implantes agora podem parcelar e realizar os procedimentos. O melhor é que recebo o valor integral em 48h e a Dr.Cash cuida de toda a gestão da cobrança."
          author={{
            name: "Dra. Maria Silva",
            role: "Clínica Odontológica Sorrir",
            avatarSrc: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=256&q=80"
          }}
        />
        
        <ProductPlanSection
          title="Planos para todas as necessidades"
          subtitle="Selecione pelo menos um serviço e um plano para iniciar seu cadastro"
        />
      </main>
      <Footer />
    </ServiceSelectionProvider>
  );
};

export default BoletoParcelado;
